import '../../sim/ai/brains';
import { type V2, angleTo, closestOnSegment, dist } from '../../core/math';
import type { Actor, CastState } from '../../sim/actor';
import type { BossMechanic, BossScript } from '../../sim/ai/brains';
import { createMonster } from '../../sim/factory';
import { registry } from '../../sim/registry';
import type { Shape } from '../../sim/shapes';
import { levelMult } from '../../sim/skills';
import type { AreaSpec, HitSpec, SkillAction, SkillDef, SkillStage, StatusApply } from '../../sim/skillTypes';
import { defenseMultiplier } from '../../sim/stats';
import type { Element, Grade, Reaction, Team } from '../../sim/types';
import type { World } from '../../sim/world';
import { defenseFor, monsterStats, playerHpAt } from '../balance';
import { area, counterWindow, hits, move, rect, skill, stage } from '../classes/kit';

/**
 * Shared runtime for endgame encounters (colossi, Deepvault, Warlord Siege).
 *
 * Multiplayer model: everything in here runs on the instance host only (the
 * peer that owns the bosses). Damage is always dealt by replicated monster
 * casts; scripts only spawn helper actors, move host-owned helpers, and grant
 * or revoke statuses through the world's routed helpers.
 *
 * The boss brain pauses while a boss is staggered or countered, so encounter
 * logic that must react immediately (grab rescues, part breaks, clone
 * counters, orb pickups) runs in a world tick hook — the "encounter watcher" —
 * installed lazily by the boss script's `tick`.
 */

// ---------------------------------------------------------------- balance

/** Reference level for damage authoring (the level cap; ratios are level-independent). */
export const REF_LEVEL = 30;
/** Player mitigation used by `classBaseStats` (defence as a fraction). */
export const PLAYER_MITIGATION = 0.12;
/** One-shot damage as a fraction of a player's max HP (raid rule: >= 150% after variance). */
export const ONE_SHOT = 2.0;

/**
 * Returns a converter from "fraction of a level-30 player's max HP" to a skill
 * coefficient for a boss of this grade and attack modifier. Monster attack and
 * player HP both grow 9% per level, so the ratio holds at any level.
 */
export function hpScale(grade: Grade, atkMod = 1): (frac: number) => number {
  const atk = monsterStats(REF_LEVEL, grade, { atk: atkMod }).attack ?? 1;
  const perCoef = atk * levelMult(1) * defenseMultiplier(defenseFor(PLAYER_MITIGATION));
  const hp = playerHpAt(REF_LEVEL);
  return (frac) => Math.round(((frac * hp) / perCoef) * 100) / 100;
}

// ---------------------------------------------------------------- shorthands

export type Tele = 'red' | 'yellow' | 'blue' | 'purple';

export const KD: Reaction = { kind: 'knockdown', duration: 1.2, power: 1 };
export const kb = (power: number): Reaction => ({ kind: 'knockback', power });
export const launch = (power = 1, duration = 1): Reaction => ({ kind: 'launch', power, duration });
export const pull = (power: number): Reaction => ({ kind: 'pull', power });

export const burn = (duration = 4, value = 0.1): StatusApply => ({ id: 'burn', duration, value });
export const stun = (duration: number): StatusApply => ({ id: 'stun', duration });
export const freeze = (duration: number): StatusApply => ({ id: 'freeze', duration });
export const chill = (duration = 3): StatusApply => ({ id: 'chill', duration });
export const shock = (duration = 4, value = 0.08): StatusApply => ({ id: 'shock', duration, value });

/** Square zone of side `size` centred `distance` m away at `angleDeg` from the caster's facing. */
export const polarSquare = (distance: number, angleDeg: number, size: number): Shape => rect(size, size, distance - size / 2, angleDeg);

/** Telegraphed eruption: a ground marker for `delay` seconds, then one hit. */
export function tele(at: number, shape: Shape, delay: number, color: Tele, coef: number, extra: Partial<HitSpec> = {}, opts: Partial<Omit<AreaSpec, 'shape' | 'hit' | 'delay' | 'telegraph'>> = {}): SkillAction {
  return area(at, { shape, delay, telegraph: color, ...opts, hit: { shape, coef, ...extra } });
}

/** Lingering ground hazard: telegraph for `delay`, then ticks every `tick` s for `duration` s. */
export function hazard(at: number, shape: Shape, delay: number, duration: number, tick: number, coef: number, color: Tele, extra: Partial<HitSpec> = {}, opts: Partial<Omit<AreaSpec, 'shape' | 'hit' | 'delay' | 'telegraph' | 'duration' | 'tickInterval'>> = {}): SkillAction {
  return area(at, { shape, delay, duration, tickInterval: tick, telegraph: color, ...opts, hit: { shape, coef, ...extra } });
}

export interface BossSkillSpec {
  id: string;
  name: string;
  cd: number;
  anim?: string;
  dur?: number;
  actions?: SkillAction[];
  stages?: SkillStage[];
  castTime?: number;
  element?: Element;
  hue?: number;
  description?: string;
  tags?: string[];
  moveMult?: number;
}

/** Monster skill with boss defaults (super armour, no mana). */
export function bskill(o: BossSkillSpec): SkillDef {
  const stages = o.stages ?? [stage(o.anim ?? 'Attack', o.dur ?? 1.2, o.actions ?? [], o.moveMult ? { moveMult: o.moveMult } : {})];
  return skill({
    id: o.id,
    name: o.name,
    description: o.description ?? o.name,
    castType: 'normal',
    cooldown: o.cd,
    mana: 0,
    kind: 'monster',
    superArmor: 'full',
    stages,
    ...(o.castTime ? { castTime: o.castTime } : {}),
    ...(o.element ? { element: o.element } : {}),
    ...(o.hue !== undefined ? { hue: o.hue } : {}),
    ...(o.tags ? { tags: o.tags } : {}),
  });
}

/**
 * Counterable charge: blue glow (counter window) during the wind-up, a red
 * lane telegraph, then a multi-hit dash. A successful counter interrupts it.
 */
export function counterCharge(o: { id: string; name: string; cd: number; distance: number; width: number; windup: number; coef: number; element?: Element; hue?: number; reaction?: Reaction; window?: number }): SkillDef {
  const w = o.windup;
  const win = o.window ?? 0.9;
  return bskill({
    id: o.id,
    name: o.name,
    cd: o.cd,
    anim: 'Charge',
    dur: w + 0.9,
    element: o.element,
    hue: o.hue,
    tags: ['counterable'],
    description: `${o.name}: glows blue before charging (counter it from the front).`,
    actions: [
      counterWindow(Math.max(0.2, w - win), win),
      tele(0, rect(o.distance + 2, o.width), w, 'red', 0),
      move(w, o.distance, 0.55),
      hits(w + 0.05, 4, 0.13, rect(3, o.width), o.coef, { reaction: o.reaction ?? KD }),
    ],
  });
}

/** Stagger check / wipe: a long cast bar, then an arena-wide one-shot. Staggering interrupts it. */
export function wipeCast(id: string, name: string, castTime: number, coef: number, radius = 50, color: Tele = 'red'): SkillDef {
  return bskill({
    id,
    name,
    cd: 0,
    castTime,
    anim: 'Slam',
    dur: 1.2,
    tags: ['oneshot', 'wipe'],
    description: `${name}: stagger the boss before the cast completes.`,
    actions: [tele(0, { kind: 'circle', radius }, 0.45, color, coef, { reaction: KD })],
  });
}

// ---------------------------------------------------------------- encounter state

export type CastOutcome = 'completed' | 'countered' | 'staggered' | 'interrupted' | 'dead';

interface Timer {
  at: number;
  fn: () => void;
}

interface QueuedCast {
  skill: string;
  point: () => V2 | null;
  notBefore: number;
  expires: number;
}

export interface CurrentCast {
  castId: number;
  skillId: string;
  /** Encounter time when the cast was first seen. */
  start: number;
  aimAngle: number;
  aimPoint: V2;
  /** Boss position at cast start. */
  origin: V2;
}

export interface EncState {
  /** Seconds since the encounter watcher first saw the boss. */
  time: number;
  arena: { center: V2; radius: number };
  timers: Timer[];
  queue: QueuedCast[];
  cur: CurrentCast | null;
  /** Neutral/static helper actors (pillars, orbs, clones...) removed when the boss dies. */
  helpers: number[];
  /** Hostile adds killed when the boss dies. */
  adds: number[];
  /** Scripted mechanic names in trigger order (tests + UI). */
  log: string[];
  /** Cast id interrupted by the script itself (e.g. pillar collision). */
  interruptedCast: number;
  data: Record<string, unknown>;
  inited: boolean;
  cleaned: boolean;
}

/** Encounter state stored on the boss's brain record (host only). */
export function enc(boss: Actor): EncState {
  const brain = boss.monster!.brain;
  let e = brain.enc as EncState | undefined;
  if (!e) {
    const radius = Number(registry.monsters.get(boss.monster!.defId)?.params?.arenaRadius ?? 20);
    e = {
      time: 0,
      arena: { center: { ...boss.monster!.home }, radius },
      timers: [],
      queue: [],
      cur: null,
      helpers: [],
      adds: [],
      log: [],
      interruptedCast: -1,
      data: {},
      inited: false,
      cleaned: false,
    };
    brain.enc = e;
  }
  return e;
}

/** Overrides the arena used by edge spawns / ring breaks (defaults to the boss's spawn point). */
export function setEncounterArena(boss: Actor, center: V2, radius: number): void {
  enc(boss).arena = { center: { ...center }, radius };
}

/** Current HP phase from the boss brain (1-based). */
export function bossPhase(boss: Actor): number {
  return (boss.monster?.brain as { phase?: number } | undefined)?.phase ?? 1;
}

export interface EncounterDef {
  init?(world: World, boss: Actor, e: EncState): void;
  watch?(world: World, boss: Actor, e: EncState, dt: number): void;
  onCastStart?: Record<string, (world: World, boss: Actor, e: EncState, cur: CurrentCast) => void>;
  onCastEnd?: Record<string, (world: World, boss: Actor, e: EncState, outcome: CastOutcome, cur: CurrentCast) => void>;
  onDeath?(world: World, boss: Actor, e: EncState): void;
  /** Player statuses cleared when the boss dies. */
  clearOnDeath?: string[];
}

const encounters = new Map<string, EncounterDef>();

export function registerEncounter(defIds: string | string[], def: EncounterDef): void {
  for (const id of Array.isArray(defIds) ? defIds : [defIds]) encounters.set(id, def);
}

const hooked = new WeakSet<World>();

/** Installs the per-world encounter watcher once (host side). */
export function ensureEncounterHook(world: World): void {
  if (hooked.has(world)) return;
  hooked.add(world);
  world.tickHooks.push(runEncounters);
}

function outcomeOf(boss: Actor, e: EncState, castId: number): CastOutcome {
  if (!boss.alive) return 'dead';
  if ((boss.monster?.countered ?? 0) > 0) return 'countered';
  if (boss.hasStatus('staggered')) return 'staggered';
  if (e.interruptedCast === castId) return 'interrupted';
  return 'completed';
}

function runEncounters(world: World, dt: number): void {
  for (const boss of [...world.actors.values()]) {
    const m = boss.monster;
    if (!m || !world.isLocal(boss)) continue;
    const def = encounters.get(m.defId);
    if (!def) continue;
    const e = enc(boss);
    if (!boss.alive) {
      if (e.inited && !e.cleaned) cleanup(world, boss, e, def);
      continue;
    }
    if (!e.inited) {
      e.inited = true;
      def.init?.(world, boss, e);
    }
    e.time += dt;

    // 1. Cast end.
    const c0 = boss.cast;
    if (e.cur && (!c0 || c0.castId !== e.cur.castId)) {
      const ended = e.cur;
      e.cur = null;
      def.onCastEnd?.[ended.skillId]?.(world, boss, e, outcomeOf(boss, e, ended.castId), ended);
    }
    // 2. Timers.
    if (e.timers.length > 0) {
      const due = e.timers.filter((t) => t.at <= e.time);
      if (due.length > 0) {
        e.timers = e.timers.filter((t) => t.at > e.time);
        for (const t of due) t.fn();
      }
    }
    // 3. Scripted cast queue (has priority over the pattern picker).
    runQueue(world, boss, e);
    // 4. Cast start.
    const c = boss.cast;
    if (c && !e.cur) {
      e.cur = { castId: c.castId, skillId: c.skill.id, start: e.time, aimAngle: c.aimAngle, aimPoint: { ...c.aimPoint }, origin: { ...boss.pos } };
      def.onCastStart?.[c.skill.id]?.(world, boss, e, e.cur);
    }
    // 5. Continuous logic.
    def.watch?.(world, boss, e, dt);
  }
}

function runQueue(world: World, boss: Actor, e: EncState): void {
  const q = e.queue[0];
  if (!q || q.notBefore > e.time) return;
  if (e.time > q.expires) {
    e.queue.shift();
    return;
  }
  // Hold the pattern picker while a scripted cast is due.
  const brain = boss.monster!.brain as { gcd?: number };
  brain.gcd = Math.max(brain.gcd ?? 0, 0.5);
  if (boss.cast) return;
  const pt = q.point();
  // Skills removed from the moveset by a part break stay removed.
  if (!pt || isLocked(boss, q.skill)) {
    e.queue.shift();
    return;
  }
  // Scripted casts ignore the pattern cooldown of skills shared with the rotation.
  boss.cooldowns.delete(q.skill);
  if (world.monsterCast(boss, q.skill, pt)) e.queue.shift();
}

function cleanup(world: World, boss: Actor, e: EncState, def: EncounterDef): void {
  e.cleaned = true;
  for (const id of e.helpers) if (world.actors.has(id)) world.removeActor(id);
  for (const id of e.adds) {
    const a = world.actors.get(id);
    if (a?.alive) world.kill(a, null);
  }
  for (const ar of world.areas) {
    if (ar.ownerId === boss.id && !ar.dead) {
      ar.dead = true;
      world.emit({ type: 'areaEnd', id: ar.id });
    }
  }
  if (def.clearOnDeath?.length) for (const p of players(world)) world.revokeStatus(p, def.clearOnDeath, boss.id);
  def.onDeath?.(world, boss, e);
}

/** Runs `fn` after `delay` seconds of encounter time (host). */
export function after(boss: Actor, delay: number, fn: () => void): void {
  const e = enc(boss);
  e.timers.push({ at: e.time + delay, fn });
}

/** True when a scripted cast of `skillId` is queued or being cast. */
export function isPending(boss: Actor, skillId: string): boolean {
  return boss.cast?.skill.id === skillId || enc(boss).queue.some((q) => q.skill === skillId);
}

/** Queues a scripted boss cast; it fires as soon as the boss is free (and `delay` has passed). */
export function castAt(boss: Actor, skillId: string, point: V2 | (() => V2 | null), delay = 0): void {
  const e = enc(boss);
  const fn = typeof point === 'function' ? point : () => point;
  e.queue.push({ skill: skillId, point: fn, notBefore: e.time + delay, expires: e.time + delay + 20 });
}

// ---------------------------------------------------------------- scripts & mechanics

/** Wraps a BossScript so its tick installs the encounter watcher. */
export function bossScript(s: BossScript): BossScript {
  return {
    ...s,
    tick: (world, boss, dt) => {
      ensureEncounterHook(world);
      s.tick?.(world, boss, dt);
    },
  };
}

/** A scripted mechanic at an HP bar count (`bars`) or HP percentage (`pct`). Logged and announced. */
export function mech(name: string, at: { bars?: number; pct?: number }, text: string, run: (world: World, boss: Actor, e: EncState) => void): BossMechanic {
  return {
    name,
    ...(at.bars !== undefined ? { atBars: at.bars } : {}),
    ...(at.pct !== undefined ? { atPct: at.pct } : {}),
    run: (world, boss) => {
      ensureEncounterHook(world);
      const e = enc(boss);
      e.log.push(name);
      announce(world, text);
      run(world, boss, e);
    },
  };
}

export function announce(world: World, text: string, style: 'mechanic' | 'warning' | 'boss' | 'info' | 'success' = 'mechanic'): void {
  world.emit({ type: 'announce', text, style });
}

// ---------------------------------------------------------------- actors & geometry

export function players(world: World): Actor[] {
  return world.aliveActors.filter((a) => a.kind === 'player' && a.alive);
}

export function around(center: V2, r: number, angle: number): V2 {
  return { x: center.x + Math.sin(angle) * r, z: center.z + Math.cos(angle) * r };
}

/** A point just north of the boss (for world-aligned casts). */
export function northOf(boss: Actor): V2 {
  return { x: boss.pos.x, z: boss.pos.z + 4 };
}

export function nearestPlayer(world: World, p: V2): Actor | null {
  let best: Actor | null = null;
  let bd = Infinity;
  for (const a of players(world)) {
    const d = dist(a.pos, p);
    if (d < bd) {
      bd = d;
      best = a;
    }
  }
  return best;
}

export function farthestPlayer(world: World, p: V2): Actor | null {
  let best: Actor | null = null;
  let bd = -1;
  for (const a of players(world)) {
    const d = dist(a.pos, p);
    if (d > bd) {
      bd = d;
      best = a;
    }
  }
  return best;
}

export function randomPlayer(world: World): Actor | null {
  const ps = players(world);
  return ps.length > 0 ? world.rng.pick(ps) : null;
}

/** Point getter tracking a player's live position (falls back to the nearest player). */
export function playerPoint(world: World, boss: Actor, id: number | null): () => V2 | null {
  return () => {
    const a = id !== null ? world.actors.get(id) : undefined;
    if (a?.alive) return { ...a.pos };
    const n = nearestPlayer(world, boss.pos);
    return n ? { ...n.pos } : null;
  };
}

export interface HelperOptions {
  team?: Team;
  facing?: number;
  /** Hostile add (counts as a real monster, killed when the boss dies). */
  add?: boolean;
  hpMult?: number;
}

/** Spawns a helper actor (pillar, orb, clone, add) owned by the host and tracked by the encounter. */
export function spawnHelper(world: World, boss: Actor, defId: string, pos: V2, opts: HelperOptions = {}): Actor {
  const e = enc(boss);
  const p = world.nav.walkable(pos) ? pos : (world.nav.nearestWalkable(pos, 12) ?? pos);
  const a = createMonster(world, defId, boss.monster!.level, p, {
    team: opts.team ?? (opts.add ? 'monsters' : 'neutral'),
    facing: opts.facing ?? angleTo(p, boss.pos),
    tags: [opts.add ? 'add' : 'helper', `enc:${boss.id}`],
    ...(opts.hpMult !== undefined ? { hpMult: opts.hpMult } : {}),
  });
  // Helpers never drift from player/monster separation.
  if (!opts.add) a.monster!.immunePush = true;
  (opts.add ? e.adds : e.helpers).push(a.id);
  return a;
}

/** Removes a helper actor (despawn, no death event). */
export function despawn(world: World, id: number): void {
  if (world.actors.has(id)) world.removeActor(id);
}

/** Alive helpers of an encounter with the given def id. */
export function helpersOf(world: World, e: EncState, defId: string | string[]): Actor[] {
  const ids = Array.isArray(defId) ? defId : [defId];
  const out: Actor[] = [];
  for (const id of [...e.helpers, ...e.adds]) {
    const a = world.actors.get(id);
    if (a?.alive && a.monster && ids.includes(a.monster.defId)) out.push(a);
  }
  return out;
}

/** True when `p` is hidden behind `pillar` as seen from `src`. */
export function behindPillar(src: V2, p: V2, pillar: V2, pillarRadius: number, depth = 9): boolean {
  if (dist(src, p) <= dist(src, pillar)) return false;
  if (dist(pillar, p) > depth) return false;
  const c = closestOnSegment(pillar, src, p);
  return dist(c, pillar) <= pillarRadius + 0.35;
}

// ---------------------------------------------------------------- statuses, stagger, cooldown locks

/** Sets a stacking status to exactly `stacks` (revoke + grant, routed to the owner). */
export function setStacks(world: World, target: Actor, id: string, stacks: number, duration: number, srcId: number): void {
  world.revokeStatus(target, [id], srcId);
  if (stacks > 0) world.grantStatus(target, { id, duration, stacks }, srcId);
}

export function stacksOf(a: Actor, id: string): number {
  return a.getStatus(id)?.stacks ?? 0;
}

/** Re-arms the stagger bar (active, `frac` of max remaining). */
export function resetStagger(boss: Actor, frac = 1): void {
  const st = boss.monster?.stagger;
  if (!st) return;
  st.active = true;
  st.recoverTimer = 0;
  st.value = Math.max(1, st.max * frac);
}

/**
 * Opens the stagger bar for a rescue/punish window without ever making it
 * harder: the remaining bar becomes at most `frac` of max.
 */
export function exposeStagger(boss: Actor, frac: number): void {
  const st = boss.monster?.stagger;
  if (!st) return;
  const cur = st.active ? st.value : st.max;
  st.active = true;
  st.recoverTimer = 0;
  st.value = Math.max(1, Math.min(cur, st.max * frac));
}

/** Makes the boss unstaggerable until `resetStagger`. */
export function lockStagger(boss: Actor): void {
  const st = boss.monster?.stagger;
  if (!st) return;
  st.active = false;
  st.recoverTimer = 0;
}

const LOCK = 1e9;
/** Removes a pattern from rotation (the brain skips skills on cooldown). */
export function lockSkill(boss: Actor, id: string): void {
  boss.cooldowns.set(id, LOCK);
}
export function unlockSkill(boss: Actor, id: string): void {
  if ((boss.cooldowns.get(id) ?? 0) > LOCK / 10) boss.cooldowns.delete(id);
}
export function isLocked(boss: Actor, id: string): boolean {
  return (boss.cooldowns.get(id) ?? 0) > LOCK / 10;
}

/** Throttle helper: true once every `interval` seconds per key. */
export function every(e: EncState, key: string, interval: number): boolean {
  const k = `__every_${key}`;
  const next = (e.data[k] as number | undefined) ?? 0;
  if (e.time < next) return false;
  e.data[k] = e.time + interval;
  return true;
}

/** Cast getter used by onCastStart handlers that must check the boss is still in that cast later. */
export function stillCasting(boss: Actor, castId: number): CastState | null {
  return boss.cast && boss.cast.castId === castId ? boss.cast : null;
}
